import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import type { PresenceStatus, PublicUser } from '@itsappy/shared';
import { cn, initials } from '@/lib/utils';
import { useUi } from '@/store/ui';
import { ConfirmContext, type ConfirmFn, type ConfirmRequest } from './confirmContext';
import { STATUS_TEXT } from './presence';
import { useDismissOnEscape } from './escape';
import {
  AlertIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
  EyeIcon,
  EyeOffIcon,
  InfoIcon,
  type IconProps,
} from './icons';

/** Any icon from `components/ui/icons`. */
export type IconComponent = React.ComponentType<IconProps>;

/* --------------------------- Password input ---------------------------- */

export function PasswordInput({
  className,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <input
        {...props}
        type={visible ? 'text' : 'password'}
        className={cn('input pr-11', className)}
      />
      <button
        type="button"
        onClick={() => setVisible((current) => !current)}
        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-lg text-slate-400 transition-colors hover:text-ink"
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
      >
        {visible ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}

/* ------------------------------- Avatar -------------------------------- */

const STATUS_DOT: Record<PresenceStatus, string> = {
  online: 'bg-emerald-400',
  busy: 'bg-red-400',
  away: 'bg-amber-400',
  in_meeting: 'bg-violet-400',
  offline: 'bg-slate-500',
};

const AVATAR_SIZES = {
  xs: 'h-5 w-5 text-[9px]',
  sm: 'h-7 w-7 text-[11px]',
  md: 'h-9 w-9 text-xs',
  lg: 'h-12 w-12 text-sm',
  xl: 'h-20 w-20 text-xl',
};

export function Avatar({
  user,
  size = 'md',
  showStatus = false,
  className,
}: {
  user: Pick<PublicUser, 'name' | 'avatarUrl' | 'avatarColor' | 'status'> &
    Pick<Partial<PublicUser>, 'flairRing' | 'doNotDisturb'>;
  size?: keyof typeof AVATAR_SIZES;
  showStatus?: boolean;
  className?: string;
}) {
  const status = user.status ?? 'offline';
  const small = size === 'xs' || size === 'sm';
  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      {/* Equipped avatar-ring flair. A background span a couple of pixels
          larger than the (opaque, fully rounded) avatar reads as a ring, and
          it takes a conic-gradient as readily as a solid colour — no border or
          box-shadow could. The value is store data, not a theme colour. */}
      {user.flairRing && (
        <span
          aria-hidden
          className={cn(
            'absolute rounded-full',
            size === 'xs' || size === 'sm' ? '-inset-[2px]' : '-inset-[2.5px]',
          )}
          style={{ background: user.flairRing }}
        />
      )}
      {user.avatarUrl ? (
        <img
          src={user.avatarUrl}
          alt=""
          loading="lazy"
          decoding="async"
          className={cn('relative rounded-full object-cover', AVATAR_SIZES[size])}
        />
      ) : (
        <span
          className={cn(
            /* Literal white: the fill is the user's own colour, not a themed surface. */
            'relative inline-flex items-center justify-center rounded-full font-semibold text-white',
            AVATAR_SIZES[size],
          )}
          style={{ backgroundColor: user.avatarColor }}
          aria-hidden
        >
          {initials(user.name)}
        </span>
      )}
      {/* Do-not-disturb replaces the dot rather than sitting beside it: the
          question the corner answers is "will they hear from me", and a moon
          answers it more plainly than a green dot with a moon next to it. */}
      {showStatus && user.doNotDisturb ? (
        <span
          className={cn(
            'absolute -bottom-0.5 -right-0.5 grid place-items-center rounded-full bg-indigo-500 text-white ring-2 ring-surface-1',
            small ? 'h-2.5 w-2.5' : 'h-3 w-3',
          )}
        >
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden
            className={small ? 'h-[7px] w-[7px]' : 'h-2 w-2'}
          >
            <path d="M12 3a9 9 0 1 0 9 9c0-.46-.04-.92-.1-1.36a5.39 5.39 0 0 1-4.4 2.26 5.4 5.4 0 0 1-3.14-9.8C12.9 3.04 12.46 3 12 3z" />
          </svg>
          <span className="sr-only">Not being disturbed</span>
        </span>
      ) : (
        showStatus && (
          <span
            className={cn(
              'absolute -bottom-0.5 -right-0.5 rounded-full ring-2 ring-surface-1',
              small ? 'h-2 w-2' : 'h-2.5 w-2.5',
              STATUS_DOT[status],
            )}
          >
            <span className="sr-only">{STATUS_TEXT[status]}</span>
          </span>
        )
      )}
    </span>
  );
}

export function AvatarStack({
  users,
  max = 3,
  size = 'sm',
}: {
  users: Pick<PublicUser, 'id' | 'name' | 'avatarUrl' | 'avatarColor' | 'status'>[];
  max?: number;
  size?: keyof typeof AVATAR_SIZES;
}) {
  const shown = users.slice(0, max);
  const overflow = users.length - shown.length;
  return (
    <div className="flex items-center -space-x-1.5" title={users.map((u) => u.name).join(', ')}>
      {shown.map((user) => (
        <Avatar
          key={user.id}
          user={user}
          size={size}
          className="rounded-full ring-2 ring-surface-1"
        />
      ))}
      {overflow > 0 && (
        <span
          className={cn(
            'inline-flex items-center justify-center rounded-full bg-surface-3 font-medium text-slate-300 ring-2 ring-surface-1',
            AVATAR_SIZES[size],
          )}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}

/* --------------------------- Overlay behaviour -------------------------- */

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Keeps Tab inside `ref` while `active`, moves focus in on open, and returns it
 * to whatever was focused before on close.
 */
export function useFocusTrap(ref: React.RefObject<HTMLElement>, active: boolean) {
  useEffect(() => {
    if (!active) return;
    const previous = document.activeElement as HTMLElement | null;
    const node = ref.current;

    const focusables = () => Array.from(node?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    (focusables()[0] ?? node)?.focus({ preventScroll: true });

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const head = items[0];
      const tail = items[items.length - 1];
      if (e.shiftKey && document.activeElement === head) {
        e.preventDefault();
        tail.focus();
      } else if (!e.shiftKey && document.activeElement === tail) {
        e.preventDefault();
        head.focus();
      }
    };

    node?.addEventListener('keydown', onKey);
    return () => {
      node?.removeEventListener('keydown', onKey);
      previous?.focus?.({ preventScroll: true });
    };
  }, [ref, active]);
}

/** Stops the page behind an overlay from scrolling, without a layout jump. */
export function useScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const { body } = document;
    const gutter = window.innerWidth - document.documentElement.clientWidth;
    const previous = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };
    body.style.overflow = 'hidden';
    if (gutter > 0) body.style.paddingRight = `${gutter}px`;
    return () => {
      body.style.overflow = previous.overflow;
      body.style.paddingRight = previous.paddingRight;
    };
  }, [active]);
}

/* -------------------------------- Modal -------------------------------- */

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = 'md',
  fullscreen = false,
  headerActions,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Fills the window instead of floating over it, for content that is worth
   * more room than a panel gives — a page of a PDF, a spreadsheet, a photo.
   * The body becomes a column so that content can be told to fill it.
   */
  fullscreen?: boolean;
  /** Controls that sit in the header, before the close button. */
  headerActions?: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useFocusTrap(panelRef, open);
  useScrollLock(open);

  useDismissOnEscape(open, onClose);

  if (!open) return null;

  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-[60] flex items-end justify-center sm:items-center',
        !fullscreen && 'sm:p-4',
      )}
    >
      <div
        className="absolute inset-0 animate-fade-in bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          'relative flex w-full animate-slide-up flex-col bg-surface-1 shadow-overlay ring-1 ring-surface-3',
          fullscreen
            ? 'h-dvh max-h-dvh rounded-none'
            : cn(
                'max-h-[92dvh] rounded-t-2xl sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl',
                widths[width],
              ),
        )}
      >
        <header className="flex shrink-0 items-start gap-3 border-b border-surface-3 px-4 py-3.5 sm:px-5 sm:py-4">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-base font-semibold text-ink">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="mt-0.5 text-sm text-slate-400">
                {description}
              </p>
            )}
          </div>
          {headerActions}
          <button
            type="button"
            onClick={onClose}
            aria-label={`Close ${title}`}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-surface-2 hover:text-ink sm:h-9 sm:w-9"
          >
            <CloseIcon size="lg" />
          </button>
        </header>
        <div
          className={cn(
            'min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5',
            fullscreen && 'flex flex-col',
          )}
        >
          {children}
        </div>
        {footer && (
          <footer className="flex shrink-0 flex-col-reverse gap-2 border-t border-surface-3 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] [&>*]:min-h-11 [&>*]:w-full sm:flex-row sm:justify-end sm:px-5 sm:[&>*]:min-h-0 sm:[&>*]:w-auto">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

/* ------------------------- Confirmation dialog ------------------------- */

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((next) => {
    /* A second request while one is open resolves the first as cancelled. */
    resolveRef.current?.(false);
    setRequest(next);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    resolveRef.current?.(value);
    resolveRef.current = null;
    setRequest(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={request !== null}
        onClose={() => settle(false)}
        title={request?.title ?? ''}
        width="sm"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary btn-touch"
              onClick={() => settle(false)}
            >
              {request?.cancelLabel ?? 'Cancel'}
            </button>
            <button
              type="button"
              className={cn(request?.destructive ? 'btn-danger' : 'btn-primary', 'btn-touch')}
              onClick={() => settle(true)}
            >
              {request?.confirmLabel ?? 'Confirm'}
            </button>
          </>
        }
      >
        <p className="text-sm leading-relaxed text-slate-300">
          {request?.description ?? 'This cannot be undone.'}
        </p>
      </Modal>
    </ConfirmContext.Provider>
  );
}

/* ------------------------------ Dropdown ------------------------------- */

export function Dropdown({
  trigger,
  children,
  align = 'right',
  className,
  label,
  triggerClassName,
}: {
  trigger: ReactNode;
  children: ReactNode | ((close: () => void) => ReactNode);
  align?: 'left' | 'right';
  className?: string;
  /** Accessible name, when the trigger's own content does not provide one. */
  label?: string;
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<CSSProperties>({});

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const gap = 6;
      const spaceBelow = window.innerHeight - rect.bottom;
      const opensUp = spaceBelow < 260 && rect.top > spaceBelow;
      const horizontal =
        align === 'right'
          ? { right: Math.max(8, window.innerWidth - rect.right) }
          : { left: Math.max(8, rect.left) };
      setPosition({
        ...horizontal,
        ...(opensUp
          ? { bottom: window.innerHeight - rect.top + gap, top: 'auto' }
          : { top: rect.bottom + gap, bottom: 'auto' }),
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.querySelector('button')?.focus();
        return;
      }
      /* Arrow keys walk the menu once it is open. */
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
      if (items.length === 0) return;
      e.preventDefault();
      const index = items.indexOf(document.activeElement as HTMLElement);
      const step = e.key === 'ArrowDown' ? 1 : -1;
      items[(index + step + items.length) % items.length]?.focus();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={triggerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        className={cn('flex w-full rounded-lg text-left', triggerClassName)}
      >
        {trigger}
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={position}
            className={cn(
              'fixed z-[80] max-h-[min(70vh,28rem)] min-w-[12rem] animate-slide-up overflow-y-auto overscroll-contain rounded-xl border border-surface-3 bg-surface-2 py-1 shadow-overlay',
              className,
            )}
          >
            {typeof children === 'function' ? children(() => setOpen(false)) : children}
          </div>,
          document.body,
        )}
    </div>
  );
}

export function MenuItem({
  children,
  onClick,
  danger,
  disabled,
  selected,
}: {
  children: ReactNode;
  onClick?: () => void;
  danger?: boolean;
  disabled?: boolean;
  selected?: boolean;
}) {
  const tone = danger
    ? 'text-red-300 hover:bg-red-500/10'
    : 'text-slate-200 hover:bg-surface-3';

  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors disabled:opacity-40',
        tone,
        selected && 'bg-surface-3/60',
      )}
    >
      {children}
    </button>
  );
}

/* ------------------------------- Toasts -------------------------------- */

const TOAST_TONES = {
  success: {
    shell: 'border-emerald-500/40 bg-emerald-500/10',
    icon: 'text-emerald-400',
    Icon: CheckCircleIcon,
  },
  error: { shell: 'border-red-500/40 bg-red-500/10', icon: 'text-red-400', Icon: AlertIcon },
  info: { shell: 'border-surface-4 bg-surface-2', icon: 'text-slate-400', Icon: InfoIcon },
} as const;

export function Toaster() {
  const { toasts, dismissToast } = useUi();

  return (
    <div
      role="region"
      aria-label="Notifications"
      className="pointer-events-none fixed bottom-4 right-4 z-[90] flex w-[min(20rem,calc(100vw-2rem))] flex-col gap-2"
    >
      {toasts.map((t) => {
        const { shell, icon, Icon } = TOAST_TONES[t.tone];
        return (
          <div
            key={t.id}
            role="status"
            aria-live={t.tone === 'error' ? 'assertive' : 'polite'}
            className={cn(
              'pointer-events-auto flex animate-slide-up items-start gap-2.5 rounded-xl border py-3 pl-3 pr-2 shadow-pop backdrop-blur',
              shell,
            )}
          >
            <span className={cn('mt-px shrink-0', icon)}>
              <Icon />
            </span>

            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-ink">{t.title}</p>
              {t.description && (
                <p className="mt-0.5 text-xs text-slate-300">{t.description}</p>
              )}
              {t.onClick && (
                <button
                  type="button"
                  onClick={() => {
                    dismissToast(t.id);
                    t.onClick?.();
                  }}
                  className="mt-2 inline-flex items-center gap-0.5 text-xs font-medium text-brand-300 hover:text-brand-200"
                >
                  {t.actionLabel ?? 'Open'}
                  <ChevronRightIcon size="sm" />
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => dismissToast(t.id)}
              aria-label="Dismiss notification"
              className="-mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink"
            >
              <CloseIcon size="sm" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------ Feedback ------------------------------- */

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'inline-block animate-spin rounded-full border-2 border-surface-4 border-t-brand-400',
        className ?? 'h-4 w-4',
      )}
      role="status"
      aria-label="Loading"
    />
  );
}

/** A shaped placeholder standing in for content that is still loading. */
export function Skeleton({ className }: { className?: string }) {
  return <span aria-hidden className={cn('skeleton block', className ?? 'h-4 w-full')} />;
}

/** A list's loading state: the shape of the rows that are coming. */
export function SkeletonRows({
  rows = 4,
  avatar = true,
  className,
}: {
  rows?: number;
  avatar?: boolean;
  className?: string;
}) {
  return (
    <div className={cn('space-y-3.5 p-4', className)} aria-busy="true">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          {avatar && <Skeleton className="h-8 w-8 shrink-0 rounded-full" />}
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-[min(60%,14rem)]" />
            <Skeleton className="h-3 w-[min(35%,8rem)]" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  /** `sm` for a narrow rail or a short panel, where the full height reads as
   *  a hole rather than as breathing room. */
  size = 'md',
  className,
}: {
  icon?: IconComponent;
  title: string;
  description?: string;
  action?: ReactNode;
  size?: 'sm' | 'md';
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center px-6 text-center',
        size === 'sm' ? 'py-8' : 'py-14',
        className,
      )}
    >
      {Icon && (
        <span
          className={cn(
            'grid place-items-center rounded-xl bg-surface-2 text-slate-400 ring-1 ring-surface-3',
            size === 'sm' ? 'mb-2.5 h-9 w-9' : 'mb-3 h-11 w-11',
          )}
        >
          <Icon size={size === 'sm' ? 18 : 22} />
        </span>
      )}
      <h3 className="text-sm font-semibold text-slate-200">{title}</h3>
      {description && (
        <p className="mt-1 max-w-[38ch] text-sm leading-relaxed text-slate-400">
          {description}
        </p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function IconButton({
  label,
  active,
  size = 'md',
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  active?: boolean;
  size?: 'sm' | 'md' | 'lg';
}) {
  const sizes = { sm: 'h-8 w-8', md: 'h-9 w-9', lg: 'h-11 w-11' };
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-lg transition-colors',
        sizes[size],
        active
          ? 'bg-brand-600 text-white hover:bg-brand-500'
          : 'text-slate-300 hover:bg-surface-3 hover:text-ink',
        className,
      )}
      {...props}
    />
  );
}

/* --------------------------------- Rail --------------------------------- */

/**
 * A horizontal strip of cards, movable with whatever the reader has.
 *
 * The scrolling is the browser's own, which already covers a finger and a
 * trackpad. What it does not cover is a plain mouse: there is no horizontal
 * wheel on one, and the rail hides its scrollbar, so a rail six screens long
 * was a rail you could see the first two cards of and no more. This adds the
 * two ways back — buttons at the ends, and a vertical wheel spent sideways
 * while the rail still has somewhere to go.
 */
export function Rail({
  children,
  className,
  label,
}: {
  children: ReactNode;
  /** Classes for the scroller itself — padding, scroll padding, height. */
  className?: string;
  /** Names the buttons: `tasks` gives "Show earlier tasks". */
  label: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [room, setRoom] = useState({ before: false, after: false });

  /* Which way the rail can still go. A pixel of slack absorbs the fractional
     offsets a zoomed page hands out, which would otherwise leave a button lit
     at an end it cannot move off. Returning the old object when nothing has
     changed lets React bail out of the render — this runs after every one of
     them, so it has to be free when the answer is the same. */
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const before = el.scrollLeft > 1;
    const after = el.scrollLeft < max - 1;
    setRoom((old) => (old.before === before && old.after === after ? old : { before, after }));
  }, []);

  /* No dependency list: a card arriving or leaving changes the answer, and
     that is a render rather than a resize or a scroll. */
  useLayoutEffect(measure);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    /* The panel itself being resized — a window drag, a column widened while
       home is being arranged — moves the far end without any scrolling. */
    const observer = new ResizeObserver(measure);
    observer.observe(el);

    /* A vertical wheel is all a plain mouse has, so spend it sideways — but
       only while the rail can still move that way, so the notch that would
       run off the end scrolls the page instead of dying here. The listener is
       active, because claiming the wheel means preventing it. */
    function onWheel(event: WheelEvent) {
      const el = ref.current;
      if (!el || event.deltaY === 0) return;
      /* A trackpad's sideways swipe and shift+wheel already do the right
         thing; stay out of the way of both. */
      if (event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      if (event.deltaY > 0 ? el.scrollLeft >= max - 1 : el.scrollLeft <= 1) return;
      event.preventDefault();
      /* Wheels report lines and pages as readily as pixels. */
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? el.clientWidth : 1;
      el.scrollLeft += event.deltaY * unit;
    }

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      observer.disconnect();
      el.removeEventListener('wheel', onWheel);
    };
  }, [measure]);

  /** Moves by whole cards — as many as fit, and never fewer than one. */
  function page(direction: 1 | -1) {
    const el = ref.current;
    if (!el) return;
    const first = el.firstElementChild;
    const gap = Number.parseFloat(getComputedStyle(el).columnGap) || 0;
    const card = first ? first.getBoundingClientRect().width + gap : 0;

    /* Counting in cards rather than pixels, and rounding to one before
       stepping, is what keeps a rail a wheel has left mid-card from staying
       mid-card for the rest of its life. */
    const to = Math.max(
      0,
      Math.min(
        card > 0
          ? (Math.round(el.scrollLeft / card) +
              direction * Math.max(1, Math.floor(el.clientWidth / card))) *
              card
          : el.scrollLeft + direction * el.clientWidth,
        el.scrollWidth - el.clientWidth,
      ),
    );

    /* Gliding a card's width says which way the rail moved, where a jump
       leaves the reader to work it out. Anyone who has asked for less motion
       gets the jump. */
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({ left: to, behavior: still ? 'auto' : 'smooth' });
  }

  return (
    <div className="relative min-w-0">
      <div ref={ref} className={cn('rail', className)} onScroll={measure}>
        {children}
      </div>

      {/* After the rail in the source, so tabbing reaches the cards first. */}
      {room.before && (
        <button
          type="button"
          onClick={() => page(-1)}
          aria-label={`Show earlier ${label}`}
          className="rail-nav left-1"
        >
          <ChevronLeftIcon size="sm" />
        </button>
      )}
      {room.after && (
        <button
          type="button"
          onClick={() => page(1)}
          aria-label={`Show more ${label}`}
          className="rail-nav right-1"
        >
          <ChevronRightIcon size="sm" />
        </button>
      )}
    </div>
  );
}

export type Tone =
  'slate' | 'brand' | 'red' | 'orange' | 'amber' | 'emerald' | 'sky' | 'violet';

export function Badge({
  children,
  tone = 'slate',
  className,
}: {
  children: ReactNode;
  tone?: Tone;
  className?: string;
}) {
  return <span className={cn('chip', `tone-${tone}`, className)}>{children}</span>;
}

/** A count pill on a saturated fill — the label stays white in both themes. */
export function CountBadge({
  count,
  max = 99,
  className,
  label,
}: {
  count: number;
  max?: number;
  className?: string;
  label?: string;
}) {
  if (count <= 0) return null;
  return (
    <span className={cn('badge-count bg-brand-600', className)}>
      {count > max ? `${max}+` : count}
      {label && <span className="sr-only"> {label}</span>}
    </span>
  );
}

/* ------------------------------- Layout -------------------------------- */

/**
 * The standard page opening: what this screen is, one line on why, and the
 * screen's primary action. Shared so headings, spacing and the action's
 * position stay identical across every page.
 */
export function PageHeader({
  title,
  description,
  action,
  className,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('mb-5 flex flex-wrap items-start gap-x-4 gap-y-3', className)}>
      <div className="min-w-0 flex-1">
        <h1 className="text-xl font-semibold text-ink">{title}</h1>
        {description && <p className="mt-1 text-sm text-slate-400">{description}</p>}
      </div>
      {/* Not `shrink-0`: an action wider than the row it shares — a dozen tabs,
          or a form that has been opened in place — has to be able to wrap to
          its own line and then fit inside it. Its automatic minimum still
          stops a button being squashed, so the narrow case is unchanged. */}
      {action && <div className="flex items-center gap-2">{action}</div>}
    </header>
  );
}

/**
 * Switches between views of the same data. Keeps every option visible rather
 * than hiding them behind a select.
 */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  label,
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
  label: string;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      /* Wraps by default. A strip of tabs is as wide as the number of views
         a page happens to have, which nobody sizes a screen to — and a row
         that runs off the edge hides the very options this control exists to
         keep visible. Wrapping costs nothing when they already fit. */
      className={cn(
        'inline-flex flex-wrap rounded-lg bg-surface-2 p-0.5 ring-1 ring-surface-3',
        className,
      )}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded-[0.4rem] px-3 py-1.5 text-xs font-medium transition-colors',
              selected ? 'bg-surface-4 text-ink' : 'text-slate-400 hover:text-slate-200',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
